import { ContractStandardField, EntityTemplate, EntityTemplateField, isContractStandardField } from "../../entityTemplates/EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "../../entityTemplates/inputFields/InputFields.types.ts";
import { CustomFieldValue } from "../contract/Contract.types.ts";

export interface ReviewableFieldPermissions {
    canAccessTags: boolean;
}

/**
 * Who can see the contract is the customer's decision, not something read from the document. The extraction
 * proposes none of these except group companies, which is left out too, so the review never changes access.
 * Kept out of the proposal as well as the review, so nothing the user was not shown gets saved.
 */
export const accessStandardFields = new Set<ContractStandardField>([
    ContractStandardField.ContractManager,
    ContractStandardField.AdditionalUserAccess,
    ContractStandardField.BusinessAreaAccess,
    ContractStandardField.ExcludedUsers,
    ContractStandardField.VisibleToAllUsers,
    ContractStandardField.LegalEntities,
]);

/**
 * Every template field the review can put side by side. A field that renders nothing on one side would
 * leave a ragged row, so it is dropped rather than shown empty.
 */
export function getReviewableTemplateFields(template: EntityTemplate | undefined, permissions: ReviewableFieldPermissions): Array<EntityTemplateField> {
    if (!template) {
        return [];
    }

    return template.steps.flatMap((step) => step.fields.filter((field) => isReviewable(field, permissions)));
}

/**
 * The custom values a save may send: only those the review showed. The contract also carries values for
 * fields since removed from its template; sent back, each would be rewritten by a review that never showed
 * it, so they are left out and the backend leaves them as they are.
 */
export function getReviewedCustomFieldValues(
    customFieldValues: Array<CustomFieldValue>,
    reviewableFields: Array<EntityTemplateField>
): Array<CustomFieldValue> {
    const reviewedIds = new Set(reviewableFields.map((field) => field.customInputFieldId).filter(Boolean));

    return customFieldValues.filter((value) => reviewedIds.has(value.customInputFieldId));
}

/**
 * The standard fields the review puts in front of the user, and so the only ones a proposal may be written
 * into. Everything else keeps the contract's value: a field the role hides is not in the template at all, one
 * the role may only read is disabled, and a proposal for either would otherwise be saved without being seen.
 */
export function getReviewableStandardFields(reviewableFields: Array<EntityTemplateField>): Set<ContractStandardField> {
    const standardFields = reviewableFields
        .map((field) => field.standardFieldIdentifier)
        .filter((identifier): identifier is ContractStandardField => identifier != null && isContractStandardField(identifier));

    return new Set(standardFields);
}

function isReviewable(field: EntityTemplateField, permissions: ReviewableFieldPermissions): boolean {
    if (field.disabled) {
        return false;
    }

    if (field.standardFieldIdentifier) {
        return isContractStandardField(field.standardFieldIdentifier) && isReviewableStandardField(field.standardFieldIdentifier, permissions);
    }

    // A calculated field derives its value from its formula and a period field from two dates, so there is nothing
    // to propose and nothing the user could revert.
    return field.customInputFieldType !== InputFieldTypeEnum.CalculatedField && field.customInputFieldType !== InputFieldTypeEnum.DatePeriodField;
}

function isReviewableStandardField(standardField: ContractStandardField, permissions: ReviewableFieldPermissions): boolean {
    if (accessStandardFields.has(standardField)) {
        return false;
    }

    switch (standardField) {
        // The documents table manages the contract's files directly; the review has no proposal for it.
        case ContractStandardField.Documents:
            return false;
        case ContractStandardField.Tags:
            return permissions.canAccessTags;
        default:
            return true;
    }
}
